import { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

function RedirectHome({ onReset }: { onReset: () => void }) {
  const navigate = useNavigate();
  useEffect(() => {
    onReset();
    navigate('/', { replace: true });
  }, [navigate, onReset]);
  return null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Report explicitly. Errors a boundary catches do not reliably reach
    // `window.onerror` (which is what PostHog's capture_exceptions hooks), and
    // the production build strips console.* via vite.config `drop: ['console']`
    // — so before this, every render crash the boundary caught was silently
    // swallowed in production. No-ops when Sentry was never initialised.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    console.error('[ErrorBoundary] Caught render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return <RedirectHome onReset={() => this.setState({ hasError: false })} />;
    }
    return this.props.children;
  }
}
